import React, { useState, useCallback, FunctionComponent, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import config from 'cityads-v3/config';
import classes from './SuggestOffer.scss';
import { sendSuggestOfferForm, clearSuggestOfferFormState } from 'cityads-v3/actions/offers'
import { yandexReachGoal } from 'cityads-v3/utils/counterUtils'
import { RootState } from 'cityads-v3/types/reducer';
import { useFormInput } from 'cityads-v3/hooks/form';
import { useFormValidator } from 'cityads-v3/hooks/validators';

import AttachFile from 'cityads-v3/components/Forms/AttachFile'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DoneIcon from '@mui/icons-material/Done';
import EmptyResults from 'cityads-v3/components/EmptyResults';
import Paper from '@mui/material/Paper'
import TextField from 'cityads-v3/components/TextField/TextField'
import Typography from '@mui/material/Typography'
import WaitingButton from 'cityads-v3/components/Buttons/WaitingButton'
import { USER_ROLE_CODE_WEBMASTER } from 'cityads-v3/reducers/auth';

export default function SuggestOffer() {
  if (!config.FEATURE_SUGGEST_OFFER_ENABLE) {
    return null;
  }
  const dispatch = useDispatch();
  const { t } = useTranslation(['Offers', 'main']);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [file, setFile] = useState<File>(null);
  const isSuggestFormSending = useSelector((state: RootState) => state.offers.isSuggestFormSending);
  const isSuggestFormSent = useSelector((state: RootState) => state.offers.isSuggestFormSent);
  const suggestFormError = useSelector((state: RootState) => state.offers.suggestFormError);
  const userRoleCode = useSelector((state: RootState) => state.auth.user?.user_role_code)

  const domain = useFormInput(['notEmpty', 'domainName'], null, '');
  const message = useFormInput(['notEmpty'], null, '');

  const validateForm = useFormValidator([
    domain,
    message
  ]);

  const handleDialogOpen = useCallback(() => {
    domain.reset();
    message.reset();
    setDialogOpen(true);
    yandexReachGoal('SUGGEST_OFFER_OPEN');
  }, [])

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setFile(null);
    setTimeout(() => {
      dispatch(clearSuggestOfferFormState());
    }, 300)
    yandexReachGoal('SUGGEST_OFFER_CLOSE');
  }, [])

  const handleSendForm = useCallback(() => {
    const isValid = validateForm();
    if (isValid) {
      dispatch(sendSuggestOfferForm(domain.value, message.value, file));
      yandexReachGoal('SUGGEST_OFFER_SEND');
    }
  }, [domain.value, message.value, file, validateForm])

  useEffect(() => {
    if (isSuggestFormSent) {
      setTimeout(() => {
        handleDialogClose();
      }, 3000)
    }
  }, [isSuggestFormSent, handleDialogClose])

  if (userRoleCode !== USER_ROLE_CODE_WEBMASTER) {
    return null;
  }

  return (
    <>
      <Button
        fullWidth
        startIcon={<img width={24} src={require('./img/star.png')} />}
        className={classes.button}
        onClick={handleDialogOpen}
      >
        {t('suggestOffer.buttonText')}
      </Button>

      <FormBodyWrapper
        onClose={handleDialogClose}
        open={dialogOpen}
      >
        <>
          <DialogTitle>{t('suggestOffer.dialogTitle')}</DialogTitle>

          <div className={classes.formContainer}>
            <TextField
              fullWidth
              label={t('suggestOffer.domain')}
              placeholder="example.com"
              variant="outlined"
              InputLabelProps={{
                shrink: true
              }}
              error={!!domain.error}
              onChange={domain.handleChange}
              value={domain.value}
              helperText={domain.error}
            />

            <TextField
              fullWidth
              multiline
              label={t('suggestOffer.inputLabel')}
              rows={4}
              placeholder={t('suggestOffer.inputPlaceholder')}
              variant="outlined"
              onChange={message.handleChange}
              value={message.value}
              error={!!message.error}
              maxLength={640}
              InputLabelProps={{
                shrink: true
              }}
              helperText={message.error}
            />

            <AttachFile
              accept="image/*"
              onChange={setFile}
              maxFileSize={2 * 1024 * 1024}
            >
              {t('suggestOffer.attachFile')}
            </AttachFile>

            {suggestFormError && (
              <Typography color="error">{t('main:forms.submitError')}</Typography>
            )}
          </div>

          <DialogActions>
            <Button
              onClick={handleDialogClose}
              variant="text"
            >
              {t('main:buttons.cancel')}
            </Button>

            <WaitingButton
              color="primary"
              variant="contained"
              onClick={handleSendForm}
              waiting={isSuggestFormSending}
              delay={0}
            >
              {t('main:forms.submit')}
            </WaitingButton>
          </DialogActions>
        </>
      </FormBodyWrapper>
    </>
  )
}

type FormBodyWrapperProps = {
  onClose(): void,
  open: boolean,
  children: React.ReactNode,
}

const FormBodyWrapper: FunctionComponent<FormBodyWrapperProps> = function ({ onClose, open, children }) {
  const { t } = useTranslation(['Offers']);
  const isMobile = useSelector((state: RootState) => state.app.isMobile);
  const isSuggestFormSent = useSelector((state: RootState) => state.offers.isSuggestFormSent);

  const content = isSuggestFormSent ? (
    <EmptyResults
      icon={<DoneIcon />}
      emptyText={t('suggestOffer.success')}
    />
  ) : (
    <>{children}</>
  )

  return (
    isMobile ?
      (
        open ? <Paper>{content}</Paper> : null
      )
      :
      (
        <Dialog
          onClose={onClose}
          open={open}
        >
          {content}
        </Dialog>
      )
  )
}
